import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  CUSTOMER_COSTING_BOUNDARY,
  CUSTOMER_COSTING_BOUNDARY_MESSAGE,
  CUSTOMER_COSTING_BOUNDARY_PATTERN,
} from './customerCostingBoundaryConstants';

const splitIdentifier = (value: string): string[] =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .split('-')
    .filter(Boolean)
    .map((token) => token.toLowerCase());

const isExchangeRateIdentifier = (tokens: string[]) =>
  tokens.some(
    (token, index) =>
      token === CUSTOMER_COSTING_BOUNDARY.ExchangeRateToken &&
      CUSTOMER_COSTING_BOUNDARY_PATTERN.ExchangeRateSuffix.test(
        tokens[index + 1] ?? '',
      ),
  );

export const isCustomerCostingIdentifier = (value: string): boolean => {
  const tokens = splitIdentifier(value);
  return (
    tokens.some((token) =>
      CUSTOMER_COSTING_BOUNDARY_PATTERN.ForbiddenFieldToken.test(token),
    ) || isExchangeRateIdentifier(tokens)
  );
};

const parseRequestUrl = (value: string): URL => {
  const origin = typeof window === 'undefined' ? CUSTOMER_COSTING_BOUNDARY.LocalOrigin : window.location.origin;
  try {
    return new URL(value, origin);
  } catch {
    return new URL(encodeURI(value), origin);
  }
};

export const isCustomerCostingUrl = (value: string): boolean => {
  const url = parseRequestUrl(value);
  const pathTokens = url.pathname
    .split('/')
    .flatMap(splitIdentifier)
    .filter(Boolean);

  if (
    pathTokens.some((token) =>
      CUSTOMER_COSTING_BOUNDARY_PATTERN.ForbiddenRouteSegment.test(token),
    ) ||
    isExchangeRateIdentifier(pathTokens)
  ) {
    return true;
  }

  return [...url.searchParams.entries()].some(
    ([key, queryValue]) =>
      isCustomerCostingIdentifier(key) ||
      CUSTOMER_COSTING_BOUNDARY_PATTERN.AudValue.test(queryValue),
  );
};

const collectForbiddenFieldPaths = (
  value: unknown,
  currentPath: string,
  result: string[],
  visited: WeakSet<object>,
) => {
  if (!value || typeof value !== 'object') {
    return;
  }

  if (visited.has(value)) {
    return;
  }
  visited.add(value);

  if (Array.isArray(value)) {
    value.forEach((item, index) => collectForbiddenFieldPaths(item, `${currentPath}[${index}]`, result, visited));
    return;
  }

  Object.entries(value as Record<string, unknown>).forEach(([key, child]) => {
    const childPath = currentPath ? `${currentPath}.${key}` : key;
    if (isCustomerCostingIdentifier(key)) {
      result.push(childPath);
    }
    collectForbiddenFieldPaths(child, childPath, result, visited);
  });
};

export const findCustomerCostingFieldPaths = (value: unknown): string[] => {
  const result: string[] = [];
  collectForbiddenFieldPaths(value, '', result, new WeakSet<object>());
  return result;
};

export const containsCustomerCostingFields = (value: unknown): boolean =>
  findCustomerCostingFieldPaths(value).length > 0;

export type CustomerBoundaryRequest = string | Pick<FetchArgs, 'url' | 'body'>;

export const getCustomerCostingBoundaryViolation = (request: CustomerBoundaryRequest): string | null => {
  const url = typeof request === 'string' ? request : request.url;
  if (isCustomerCostingUrl(url)) {
    return CUSTOMER_COSTING_BOUNDARY_MESSAGE.blockedRoute(url);
  }

  if (typeof request !== 'string') {
    const forbiddenPaths = findCustomerCostingFieldPaths(request.body);
    if (forbiddenPaths.length > 0) {
      return CUSTOMER_COSTING_BOUNDARY_MESSAGE.blockedFields(forbiddenPaths);
    }
  }

  return null;
};

export const customerCostingBoundaryError = (
  request: CustomerBoundaryRequest,
): FetchBaseQueryError | null => {
  const violation = getCustomerCostingBoundaryViolation(request);
  if (!violation) {
    return null;
  }

  return {
    status: CUSTOMER_COSTING_BOUNDARY.ErrorStatus,
    error: violation,
  };
};
